import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import StarField from '../components/StarField';
import PhoneFrame from '../components/PhoneFrame';
import { uploadImage } from '../lib/orbitApi';
import { PRIVACY_OPTIONS, FEELS } from '../mock';
import { X, Star, Camera, Smile, MapPin, ChevronDown, BarChart3, Plus, ChevronRight } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function Compose() {
  const nav = useNavigate();
  const { addPost, profile, galaxies, loadGalaxies } = useApp();
  const fileRef = useRef(null);

  useEffect(() => { loadGalaxies(); }, [loadGalaxies]);

  const [text, setText] = useState('');
  const [galaxy, setGalaxy] = useState(null); // galaxy id
  const [galaxyName, setGalaxyName] = useState(null);
  const [privacy, setPrivacy] = useState(PRIVACY_OPTIONS[0]);
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [feel, setFeel] = useState(null);
  const [location, setLocation] = useState(null);
  const [showMore, setShowMore] = useState(true);
  const [showPoll, setShowPoll] = useState(false);
  const [pollQ, setPollQ] = useState('');
  const [opts, setOpts] = useState(['', '', '']);
  const [launching, setLaunching] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setImage(url);
      toast('Media attached');
    } catch (err) {
      toast(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const launch = async () => {
    if (!text.trim() && !image && !showPoll) { toast('Add a thought before you launch'); return; }
    if (privacy === 'Public Galaxy' && !galaxy) { toast('Pick a Galaxy destination first'); return; }

    setLaunching(true);
    try {
      const poll = showPoll && pollQ.trim() ? { question: pollQ, options: opts.filter((o) => o.trim()) } : null;
      await addPost({ text, galaxy, privacy, image, feel, location, poll });
      toast('Broadcast launched into orbit 🚀');
      nav('/home');
    } catch (err) {
      toast(err.message || 'Could not launch');
    } finally {
      setLaunching(false);
    }
  };

  const optPill = { width: '100%', background: 'transparent', color: '#ffd9f3', borderRadius: 999,
    padding: '11px 18px', outline: 'none', border: '1.5px solid rgba(255,157,224,.7)' };

  return (
    <PhoneFrame>
      <div className="relative min-h-full">
        <StarField />
        <div className="relative z-10 pb-10">
          {/* Header */}
          <div className="orbit-page-header justify-between">
            <button onClick={() => nav(-1)} className="press orbit-icon-button" aria-label="Close composer">
              <X />
            </button>
            <h1 className="font-display orbit-page-title flex-1">New Broadcast</h1>
            <button onClick={launch} disabled={launching} className="press orbit-primary-button font-display" style={{ opacity: launching ? 0.6 : 1 }}>
              <Star fill="#fff" /> {launching ? '…' : 'Launch'}
            </button>
          </div>

          {/* Author + galaxy */}
          <div className="flex items-start gap-3 px-4 pt-4">
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" style={{ border: '1px solid rgba(160,140,230,.4)' }} />
            ) : (
              <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold" style={{ background: 'linear-gradient(135deg,#ff5bd1,#6b4fd6)', color: '#fff' }}>
                {(profile?.display_name || profile?.username || '?')[0]?.toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold" style={{ color: '#efeaff' }}>@{profile?.username}</span>
                <ChevronRight size={16} style={{ color: '#6f6795' }} />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="press" style={{ color: galaxy ? '#ff8fe4' : '#7d769f' }}>{galaxyName || 'Add Galaxy destination'}</button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#171a38] border-[#2a2d55] text-[#e6ddff]">
                    {galaxies.length === 0 && <DropdownMenuItem disabled>No galaxies yet</DropdownMenuItem>}
                    {galaxies.map((g) => (
                      <DropdownMenuItem key={g.id} onClick={() => { setGalaxy(g.id); setGalaxyName(g.name); setPrivacy('Public Galaxy'); }} className="capitalize focus:bg-[#2a2d55]">
                        {g.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
                placeholder="⭐  Launch a thought" className="w-full bg-transparent outline-none resize-none mt-1"
                style={{ color: '#ece8fb', fontSize: 18 }} />
            </div>
          </div>

          {image && (
            <div className="mx-4 mt-2 relative overflow-hidden" style={{ borderRadius: 18 }}>
              <img src={image} alt="" className="w-full object-cover" style={{ maxHeight: 240 }} />
              <button onClick={() => setImage(null)} className="absolute top-2 right-2 bg-black/60 rounded-full p-1"><X size={16} color="#fff" /></button>
            </div>
          )}

          {/* Privacy pill */}
          <div className="px-4 mt-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="press orbit-secondary-button !px-4 italic"
                  style={{ color: '#efe4ff' }}>
                  <Star fill="#ff5bd1" color="#ff5bd1" /> {privacy} <ChevronDown />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#171a38] border-[#2a2d55] text-[#e6ddff]">
                {PRIVACY_OPTIONS.map((p) => (
                  <DropdownMenuItem key={p} onClick={() => setPrivacy(p)} className="focus:bg-[#2a2d55]">{p}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Media / Feels / Location */}
          <div className="px-4 mt-4 space-y-3">
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
            <Row icon={<Camera size={20} />} onClick={() => fileRef.current?.click()} label={uploading ? 'Uploading…' : 'Media'} active={!!image} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full text-left"><Row icon={<Smile size={20} />} label={feel ? `Feeling ${feel}` : 'Feels'} active={!!feel} asDiv /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#171a38] border-[#2a2d55] text-[#e6ddff] grid grid-cols-2">
                {FEELS.map((f) => <DropdownMenuItem key={f} onClick={() => setFeel(f)} className="capitalize focus:bg-[#2a2d55]">{f}</DropdownMenuItem>)}
              </DropdownMenuContent>
            </DropdownMenu>
            <Row icon={<MapPin size={20} />} label={location || 'Location'} active={!!location}
              onClick={() => { const l = prompt('Where are you broadcasting from?'); if (l) setLocation(l); }} />
          </div>

          {/* Divider */}
          <div className="relative flex items-center px-6 my-5">
            <div className="star-line flex-1" /><Star size={20} fill="#e7dcff" color="#e7dcff" className="mx-2" /><div className="star-line flex-1" />
          </div>

          {/* More Options */}
          <button onClick={() => setShowMore((s) => !s)} className="press px-4 flex items-center gap-2 font-display font-bold text-xl" style={{ color: '#f3ecff' }}>
            More Options <ChevronDown size={20} style={{ transform: showMore ? 'rotate(180deg)' : 'none', transition: '.2s' }} />
          </button>

          {showMore && (
            <div className="px-4 mt-3">
              {/* Poll */}
              <div className="flex items-start justify-between">
                <div className="flex gap-2">
                  <BarChart3 size={20} color="#b98cff" />
                  <div>
                    <div className="font-display font-bold text-glow-pink" style={{ color: '#ff9de0', fontSize: 20 }}>Add a Poll</div>
                    <div className="italic text-sm" style={{ color: '#a89fc9' }}>Get opinions from your friends!</div>
                  </div>
                </div>
                <button onClick={() => setShowPoll((s) => !s)} className="press orbit-secondary-button !px-3">
                  <Plus /> Create Poll
                </button>
              </div>

              {showPoll && (
                <div className="mt-3 space-y-3">
                  <input value={pollQ} onChange={(e) => setPollQ(e.target.value)} placeholder="⭐  What are you feeling right now?"
                    className="w-full outline-none" style={{ background: 'rgba(120,110,190,.35)', color: '#efeaff', borderRadius: 999, padding: '12px 18px' }} />
                  <div className="pl-4 space-y-3" style={{ borderLeft: '2px solid rgba(255,91,209,.6)' }}>
                    {opts.map((o, i) => (
                      <input key={i} value={o} placeholder={['Yes', 'No', 'Maybe...'][i] || 'Option'}
                        onChange={(e) => setOpts((x) => x.map((v, j) => (j === i ? e.target.value : v)))}
                        className="placeholder:text-[#9d94c0]" style={optPill} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </PhoneFrame>
  );
}

function Row({ icon, label, onClick, active, asDiv }) {
  const inner = (
    <div className="flex items-center gap-3">
      <span className="flex items-center justify-center rounded-full" style={{ width: 40, height: 40, border: '1px solid rgba(183,165,255,.5)', color: '#cdb8ff' }}>{icon}</span>
      <span className="px-5 py-2 rounded-lg font-semibold italic" style={{ background: active ? 'rgba(255,91,209,.25)' : 'rgba(75,63,143,.55)', color: '#efe4ff', border: '1px solid rgba(139,124,232,.5)' }}>{label}</span>
    </div>
  );
  if (asDiv) return inner;
  return <button onClick={onClick} className="press w-full text-left">{inner}</button>;
}
